import { supabase, supabaseAuth } from '../supabaseClient';

// Check if we're in production (Railway deployment) or forcing production mode
const urlParams = new URLSearchParams(window.location.search);
const forceProduction = urlParams.get('production') === 'true';
const isProduction = window.location.hostname !== 'localhost' || forceProduction;

// Determine API URL
export const API_BASE_URL = isProduction 
  ? 'https://api.casekaizen.com/api'  // Production API (hardcoded for now)
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api'); // Development API



// Debug API URL construction
console.log('🔧 API URL Debug:', {
  isProduction,
  hostname: window.location.hostname,
  forceProduction: urlParams.get('production') === 'true',
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  API_BASE_URL,

  exampleUrl: `${API_BASE_URL}/cases`
});

class ApiClient {
  private async request(endpoint: string, options: RequestInit = {}) {
    // Get the current session from Supabase
    const { data: { session }, error } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;
    
    console.log('🔐 Auth Debug:', {
      hasSession: !!session,
      hasToken: !!token,
      tokenLength: token?.length,
      tokenStart: token?.substring(0, 20) + '...',
      error: error?.message,
      endpoint,
      userId: session?.user?.id,
      userEmail: session?.user?.email
    });
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const url = `${API_BASE_URL}${endpoint}`;
    console.log('🌐 Making API request:', {
      url,
      method: config.method || 'GET',
      hasBody: !!config.body
    });

    const response = await fetch(url, config);
    
    console.log('📡 API Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url: response.url
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ API Error Response:', errorData);
      
      // Create error object with response data attached (for 402 Payment Required, etc.)
      const error: any = new Error(errorData.error || `API Error: ${response.statusText}`);
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: errorData
      };
      throw error;
    }
    
    // Handle empty responses (like 204 No Content for DELETE operations)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null;
    }
    
    return response.json();
  }

  // Cases
  async getCases() {
    return this.request('/cases');
  }

  async createCase(caseData: any) {
    return this.request('/cases', {
      method: 'POST',
      body: JSON.stringify(caseData),
    });
  }

  async updateCase(id: string, updates: any) {
    return this.request(`/cases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCase(id: string) {
    return this.request(`/cases/${id}`, {
      method: 'DELETE',
    });
  }

  // Documents
  async uploadDocument(caseId: string, documentType: string, file: File, forceUpload: boolean = false) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (forceUpload) {
      formData.append('forceUpload', 'true');
    }

    // Get the current session from Supabase
    const { data: { session } } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;

    const config = {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        // Don't set Content-Type for FormData - let browser set it
      },
      body: formData,
    };

    const response = await fetch(`${API_BASE_URL}/documents/case/${caseId}/upload`, config);
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      const errorData = await response.json().catch(() => ({}));
      
      // Handle quality check errors with detailed information
      if ((response.status === 400 || response.status === 422) && errorData.qualityReport) {
        const qualityError = new Error(errorData.message || 'Document quality check failed');
        (qualityError as any).qualityReport = errorData.qualityReport;
        (qualityError as any).requiresConfirmation = errorData.requiresConfirmation || false;
        
        // DEBUG: Log what we're about to throw
        console.log('🔍 API CLIENT DEBUG - Throwing quality error:', {
          message: qualityError.message,
          requiresConfirmation: errorData.requiresConfirmation,
          hasQualityReport: !!errorData.qualityReport,
          status: response.status,
          errorData: errorData
        });
        
        throw qualityError;
      }
      
      throw new Error(errorData.error || `API Error: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getDocumentsForCase(caseId: string) {
    return this.request(`/documents/case/${caseId}`);
  }

  async deleteDocument(documentId: string) {
    return this.request(`/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async downloadDocument(documentId: string) {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/documents/${documentId}/download`, {
      headers: authHeaders,
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return response.blob();
  }

  async downloadAllDocuments(caseId: string) {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/documents/case/${caseId}/download-all`, {
      headers: authHeaders,
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return response.blob();
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  async processOCR(documentId: string) {
    return this.request(`/documents/${documentId}/ocr`, {
      method: 'POST',
    });
  }

  // Client Management
  async getClients() {
    return this.request('/clients');
  }

  async getClientsWithActiveCases() {
    return this.request('/clients/active');
  }

  async getClient(clientId: string) {
    return this.request(`/clients/${clientId}`);
  }

  async createClient(clientData: any) {
    return this.request('/clients', {
      method: 'POST',
      body: JSON.stringify(clientData),
    });
  }

  async updateClient(clientId: string, updates: any) {
    return this.request(`/clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteClient(clientId: string) {
    return this.request(`/clients/${clientId}`, {
      method: 'DELETE',
    });
  }

  // OCR and Client Summary
  async generateClientSummary(clientId: string) {
    console.log('🌐 API: Calling generateClientSummary with clientId:', clientId);
    const response = await this.request(`/ocr/generate-client-summary`, {
      method: 'POST',
      body: JSON.stringify({ clientId }),
    });
    console.log('🌐 API: generateClientSummary response:', response);
    return response;
  }

  async getClientSummary(clientId: string) {
    const response = await this.request(`/ocr/client/${clientId}/summary`);
    console.log('🌐 API getClientSummary response:', response);
    // The backend returns { success: true, data: summary, timestamp: ... }
    // We need to extract the data field
    return response.data || response;
  }

  async downloadClientSummary(summaryId: string) {
    const authHeaders = await this.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/ocr/summary/${summaryId}/download`, {
      headers: authHeaders,
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return response.blob();
  }

  // Document status methods (Phase 1)
  async getDocumentStatus(documentId: string) {
    return this.request(`/documents/${documentId}/status`);
  }

  async updateDocumentStatus(documentId: string, status: string) {
    return this.request(`/documents/${documentId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Document Templates
  async getDocumentTemplates() {
    return this.request('/documents/templates');
  }

  async createDocumentTemplate(template: any) {
    return this.request('/documents/templates', {
      method: 'POST',
      body: JSON.stringify(template),
    });
  }

  async updateDocumentTemplate(id: string, updates: any) {
    return this.request(`/documents/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteDocumentTemplate(id: string) {
    return this.request(`/documents/templates/${id}`, {
      method: 'DELETE',
    });
  }

  // Selected Document Categories
  async getSelectedCategories(caseId: string) {
    return this.request(`/selected-categories/case/${caseId}`);
  }

  async addSelectedCategory(caseId: string, categoryData: {
    category_id: string;
    category_name: string;
    category_description?: string;
    category_icon?: string;
    category_type: 'standard' | 'custom';
    created_by: string;
  }) {
    return this.request(`/selected-categories/case/${caseId}`, {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateSelectedCategory(categoryId: string, updates: {
    category_name?: string;
    category_description?: string;
    category_icon?: string;
    status?: 'pending' | 'uploaded';
    is_required?: boolean;
  }) {
    return this.request(`/selected-categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async removeSelectedCategory(categoryId: string) {
    return this.request(`/selected-categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // PDF Merging
  async mergePDFDocuments(mergeData: {
    compressionSizeMB?: number;
    customFileName?: string;

    caseId: string;
    categoryId: string;
    documentIds: string[];
    mergeOrder: 'date' | 'filename' | 'custom';
    customOrder?: string[];
  }) {
    return this.request('/pdf-merging/merge', {
      method: 'POST',
      body: JSON.stringify(mergeData),
    });
  }

  async getMergedDocuments(caseId: string, categoryId: string) {
    return this.request(`/pdf-merging/case/${caseId}/category/${categoryId}`);
  }

  async downloadMergedDocument(mergedDocumentId: string) {
    const authHeaders = await this.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/pdf-merging/download/${mergedDocumentId}`, {
      headers: authHeaders,
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return response.blob();
  }

  async deleteMergedDocument(mergedDocumentId: string) {
    return this.request(`/pdf-merging/${mergedDocumentId}`, {
      method: 'DELETE',
    });
  }

  // Forms
  async getRecommendedForms(caseTypeId: string, country: string, forceRefresh: boolean = false) {
    const url = forceRefresh 
      ? `/forms/recommended/${caseTypeId}?country=${country}&forceRefresh=true`
      : `/forms/recommended/${caseTypeId}?country=${country}`;
    return this.request(url);
  }

  async createUserForm(caseId: string, templateId: string) {
    return this.request('/forms/create', {
      method: 'POST',
      body: JSON.stringify({ caseId, templateId }),
    });
  }

  async getUserForms(caseId: string, forceRefresh: boolean = false) {
    const url = forceRefresh ? `/forms/case/${caseId}?forceRefresh=true` : `/forms/case/${caseId}`;
    return this.request(url);
  }

  async autoFillForm(userFormId: string) {
    return this.request(`/forms/${userFormId}/auto-fill`, {
      method: 'POST',
    });
  }

  async downloadForm(userFormId: string) {
    return this.request(`/forms/${userFormId}/download`);
  }

  async deleteUserForm(userFormId: string) {
    return this.request(`/forms/${userFormId}`, {
      method: 'DELETE',
    });
  }

  async searchCustomForms(query: string) {
    return this.request(`/forms/search?q=${encodeURIComponent(query)}`);
  }

  // Intake Forms
  async getIntakeForms() {
    return this.request('/intake-forms');
  }

  async createIntakeForm(formData: any) {
    return this.request('/intake-forms', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  async sendIntakeFormEmail(formId: string, emailData: { clientEmail: string; clientName: string; formTitle: string; formUrl: string; }) {
    return this.request(`/intake-forms/${formId}/send-email`, {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  }

  async getIntakeFormSubmissions() {
    return this.request('/intake-forms/submissions');
  }

  async getIntakeFormSubmissionsByFormId(formId: string) {
    return this.request(`/intake-forms/${formId}/submissions`);
  }

  async getFormSubmissionAccessCode(submissionId: string) {
    return this.request(`/intake-forms/submissions/${submissionId}/access-code`);
  }

  async getSubmissionDetails(submissionId: string) {
    return this.request(`/intake-forms/submissions/${submissionId}`);
  }

  async getIntakeForm(formId: string) {
    return this.request(`/intake-forms/${formId}`);
  }

   async runAudit(caseId: string) {
    return this.request(`/audit/run/${caseId}`, {
      method: 'POST'
    });
  }

  async updateIntakeForm(formId: string, updates: any) {
    return this.request(`/intake-forms/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteIntakeForm(formId: string) {
    return this.request(`/intake-forms/${formId}`, {
      method: 'DELETE',
    });
  }

  // Public intake form methods (no authentication required)
  async getPublicIntakeForm(formId: string) {
    return this.request(`/intake-forms/public/${formId}`);
  }

  async submitIntakeForm(formId: string, formData: any) {
    return this.request(`/intake-forms/${formId}/submit`, {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  // Form Code methods
  async generateFormCode(formId: string) {
    return this.request('/form-codes/generate', {
      method: 'POST',
      body: JSON.stringify({ formId }),
    });
  }

  async getFormSessions() {
    return this.request('/form-codes/sessions');
  }

  async validateFormCode(code: string) {
    return this.request(`/form-codes/validate/${code}`);
  }

  async loadFormByCode(code: string) {
    return this.request(`/form-codes/form/${code}`);
  }

  async saveFormData(code: string, formData: any) {
    return this.request(`/form-codes/form/${code}/save`, {
      method: 'POST',
      body: JSON.stringify({ formData }),
    });
  }

  async submitFormByCode(code: string, formData?: any) {
    return this.request(`/form-codes/form/${code}/submit`, {
      method: 'POST',
      body: formData ? JSON.stringify({ formData }) : undefined,
    });
  }

  async expireFormSession(sessionId: string, reason?: string) {
    return this.request(`/form-codes/sessions/${sessionId}/expire`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'Manually expired by lawyer' }),
    });
  }

  // Get existing client summary for a specific lawyer and form
  async getExistingClientSummary(lawyerId: string, formId: string) {
    return this.request(`/client-summary/existing/${lawyerId}/${formId}`);
  }

  // Check if form supports autofill
  async isFormAutofillSupported(formName: string, formType: string) {
    return this.request(`/forms/autofill-support?formName=${encodeURIComponent(formName)}&formType=${encodeURIComponent(formType)}`);
  }

  // Intake Form Document Upload APIs
  async uploadIntakeFormDocument(formSessionId: string, formData: FormData) {
    return this.request(`/intake-form-documents/sessions/${formSessionId}/documents`, {
      method: 'POST',
      headers: {}, // Remove Content-Type to let browser set it for FormData
      body: formData,
    });
  }

  async getIntakeFormDocuments(formSessionId: string) {
    return this.request(`/intake-form-documents/sessions/${formSessionId}/documents`);
  }

  async deleteIntakeFormDocument(documentId: string) {
    return this.request(`/intake-form-documents/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  // Create case from intake form submission
  async createCaseFromSubmission(submissionId: string) {
    return this.request(`/intake-forms/submissions/${submissionId}/create-case`, {
      method: 'POST',
    });
  }

  // Generic methods for consultation forms
  async get(endpoint: string) {
    return this.request(endpoint);
  }

  async post(endpoint: string, data?: any) {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: any) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // User Profile Management
  async getUserProfile() {
    return this.request('/users/profile');
  }

  async updateUserProfile(profileData: any) {
    return this.request('/users/profile', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }

  async uploadProfileImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const { data: { session } } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/users/profile/avatar`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload profile image');
    }

    return response.json();
  }

  async uploadFirmLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const { data: { session } } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/users/profile/firm-logo`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload firm logo');
    }

    return response.json();
  }

  async deleteProfileImage() {
    const { data: { session } } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/users/profile/avatar`, {
      method: 'DELETE',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to delete profile image');
    }

    return response.json();
  }

  async deleteFirmLogo() {
    const { data: { session } } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/users/profile/firm-logo`, {
      method: 'DELETE',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to delete firm logo');
    }

    return response.json();
  }

  async checkProfileComplete() {
    return this.request('/users/profile/complete');
  }

  async publicSubmitFirmLink(linkId: string, payload: any) {
    const response = await fetch(`${API_BASE_URL}/firm-link/${linkId}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to submit consultation form.');
    }

    return response.json();
  }

  async publicSubmitExistingForm(formId: string, payload: any) {
    const response = await fetch(`${API_BASE_URL}/consultation-forms/${formId}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to submit consultation form.');
    }

    return response.json();
  }

  async publicGetFirmTemplates(linkId: string, params: URLSearchParams) {
    const response = await fetch(`${API_BASE_URL}/firm-link/${linkId}/templates?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to fetch firm templates.');
    }
    return response.json();
  }

  async publicGetPracticeAreas(linkId: string) {
    const response = await fetch(`${API_BASE_URL}/firm-link/${linkId}/practice-areas`);
    if (!response.ok) {
      throw new Error('Failed to fetch practice areas.');
    }
    return response.json();
  }

  async getFirmLink() {
    return this.request('/firm-link');
  }

  async getFirmLinkQRCode() {
    return this.request('/firm-link/qrcode');
  }
}



export const apiClient = new ApiClient();